/**
 * Vessels Discipline Configuration
 * 
 * This configuration defines all aspects of the Vessels matrix:
 * - Column definitions
 * - Field mappings (frontend <-> backend)
 * - Calculations
 * - API endpoints
 * - Permissions
 * - Available features
 * 
 * Note: Vessels is a mechanical sub-table. It has its own:
 * - Template
 * - Rules of Credit table (rules_of_credit_vessels)
 * - Column configuration
 */

/**
 * Field mapping from frontend camelCase to backend snake_case
 */
export const FIELD_MAP = {
  buildingNumber: 'building_number',
  sovCode: 'sov_code',
  wbsCode: 'wbs_code',
  area: 'area',
  subArea: 'sub_area',
  elevation: 'elevation',
  floor: 'floor',
  equipmentTagOrCode: 'equipment_tag_or_code',
  description: 'description',
  equipmentType: 'equipment_type',
  equipment_type: 'equipment_type',
  budgetedHr: 'budgeted_hr',
  credit: 'credit',
  costCode: 'cost_code',
  dateEquipmentReceived: 'date_equipment_received',
  equipmentReceiptPercent: 'equipment_receipt_percent',
  deliveryToSite: 'delivery_to_site',
  roughSet: 'rough_set',
  supports: 'supports',
  bolting: 'bolting',
  accessories: 'accessories',
  grout: 'grout',
  measureAnchorBushHammer: 'measure_anchor_bush_hammer',
  vesselCloseOut: 'vessel_close_out',
  insulation: 'insulation',
  drawingNumber: 'drawing_number',
  pAndId: 'p_and_id',
  divisionArea: 'division_area',
  subDivisionArea: 'sub_division_area',
  activityId: 'activity_id',
  activityName: 'activity_name',
  comments: 'comments',
  udf01: 'udf01',
  udf02: 'udf02',
  udf03: 'udf03',
  udf04: 'udf04',
  udf05: 'udf05',
  udf06: 'udf06',
  udf07: 'udf07',
  udf08: 'udf08',
  udf09: 'udf09',
  udf10: 'udf10',
};

/**
 * Columns that affect the total_field_completion calculation
 * Note: total_field_completion is calculated by DB trigger (compute_vessels_earned_hours)
 * Only VISIBLE columns are included in the calculation (hidden columns excluded per 2026-01-22 spec)
 * Default weights: 20% each (equal distribution)
 */
export const FIELD_COMPLETION_COLUMNS = [
  'measureAnchorBushHammer',  // Layout/Anchor/Bush Hammer - 20%
  'roughSet',                  // Rough Set - 20%
  'grout',                     // Grout - 20%
  'vesselCloseOut',            // Vessel Close Out - 20%
  'insulation',                // Insulation - 20%
  // Hidden from calculation (data preserved in DB):
  // 'deliveryToSite', 'supports', 'bolting', 'accessories'
];

/**
 * Editable columns for progress tracking
 * Note: Location/identification columns (drawingNumber, area, subArea, elevation, floor,
 * equipmentTagOrCode, description) are READ ONLY - they are set via import only.
 */
export const EDITABLE_COLUMNS = [
  'activityId', 'activityName',
  'buildingNumber', 'sovCode', 'wbsCode', 'budgetedHr', 'credit', 'costCode',
  'dateEquipmentReceived', 'equipmentReceiptPercent', 'equipmentType',
  'deliveryToSite', 'roughSet', 'supports', 'bolting', 'accessories', 'grout',
  'measureAnchorBushHammer', 'vesselCloseOut', 'insulation',
  'pAndId', 'divisionArea', 'subDivisionArea',
  'comments', 'udf01', 'udf02', 'udf03', 'udf04', 'udf05',
  'udf06', 'udf07', 'udf08', 'udf09', 'udf10',
];

/**
 * Transform raw API data to frontend format
 */
export const transformMatrixData = (matrixData) => {
  return matrixData.map((row, index) => {
    const transformed = {
      id: row.id || index,
      area: row.area || '',
      subArea: row.sub_area || '',
      buildingNumber: row.building_number || '',
      sovCode: row.sov_code || '',
      sovDescription: row.sov_description || '',
      wbsCode: row.wbs_code || '',
      floor: row.floor || '',
      divisionArea: row.division_area || '',
      subDivisionArea: row.sub_division_area || '',
      jobNumber: row.job_number || '',
      activityId: row.activity_id || '',
      activityName: row.activity_name || '',
      costCode: row.cost_code || '',
      elevation: row.elevation || '',
      pAndId: row.p_and_id || '',
      drawingNumber: row.drawing_number || '',
      equipmentTagOrCode: row.equipment_tag_or_code || '',
      description: row.description || '',
      equipmentType: row.equipment_type || '',
      dateEquipmentReceived: row.date_equipment_received || '',
      equipmentReceiptPercent: row.equipment_receipt_percent ? Math.round(row.equipment_receipt_percent) : 0,
      deliveryToSite: row.delivery_to_site ? Math.round(row.delivery_to_site) : 0,
      roughSet: row.rough_set ? Math.round(row.rough_set) : 0,
      supports: row.supports ? Math.round(row.supports) : 0,
      bolting: row.bolting ? Math.round(row.bolting) : 0,
      accessories: row.accessories ? Math.round(row.accessories) : 0,
      grout: row.grout ? Math.round(row.grout) : 0,
      measureAnchorBushHammer: row.measure_anchor_bush_hammer ? Math.round(row.measure_anchor_bush_hammer) : 0,
      vesselCloseOut: row.vessel_close_out ? Math.round(row.vessel_close_out) : 0,
      insulation: row.insulation ? Math.round(row.insulation) : 0,
      totalFieldCompletion: row.total_field_completion ? Math.round(row.total_field_completion) : 0,
      budgetedHr: row.budgeted_hr ? Number(row.budgeted_hr) : 0,
      credit: row.credit ? Number(row.credit) : 0,
      earnedHr: row.earned_hr ? Number(row.earned_hr) : 0,
      earnedCredit: row.earned_credit ? Number(row.earned_credit) : 0,
      createdAt: row.created_at || '',
      updatedAt: row.updated_at || '',
      comments: row.comments || '',
    };
    
    // Add UDF columns
    for (let i = 1; i <= 10; i++) {
      const udfKey = `udf${String(i).padStart(2, '0')}`;
      transformed[udfKey] = row[udfKey] || '';
    }
    
    return transformed;
  });
};

/**
 * Build change payload for saving a cell change
 */
export const buildChangePayload = (rowId, columnId, oldValue, newValue) => {
  const backendField = FIELD_MAP[columnId];
  if (!backendField) return null;
  return { id: rowId, changes: { [backendField]: newValue } };
};

/**
 * Vessels discipline configuration
 */
export const vesselsConfig = {
  id: 'vessels',
  name: 'Vessels',
  menuId: 'mechanical-vessels',

  // Pin the selection checkbox + Equipment Tag + Description columns on horizontal scroll
  frozenColumnIds: ['equipmentTagOrCode', 'description'],
  freezeActionColumns: true,
  tableName: 'vessels_tracking',
  historyTableName: 'vessels_tracking_history',
  
  // API endpoints - use dedicated vessels routes under /mechanical/vessels
  api: {
    getData: '/mechanical/vessels/project/{projectId}',
    saveChanges: '/mechanical/vessels/save-changes',
    export: '/mechanical/vessels/export/{projectId}',
    upload: '/mechanical/vessels/upload-parsed',
    template: '/mechanical/vessels/template/{projectId}',
    priorities: '/priorities',
    columnMappings: '/mechanical/vessels/project/{projectId}/column-mappings',
    progressHistory: '/mechanical/vessels/{matrixId}/progress-history',
  },
  
  // Permission requirements (pt_* = Progress Tracking module)
  permissions: {
    edit: 'pt_edit',
    analytics: 'pt_analytics',
    import: 'pt_import',
    export: 'pt_export',
    template: 'pt_template',
    columnConfig: 'pt_columnconfig',
    roc: 'pt_roc',
    sfRatio: 'pt_sfratio',
    matBore: 'pt_matbore',
    delete: 'pt_delete',
  },
  
  // Column definitions
  // showInDashboard: true = visible in matrix UI
  // showInDashboard: false = hidden from UI but available for import/export
  columns: [
    // === VISIBLE COLUMNS ===
    // Drawing reference (first column) - READ ONLY (set via import)
    { id: 'drawingNumber', header: 'Drawing Number', accessor: 'drawingNumber', dbField: 'drawing_number', width: 100, editable: false, filterable: true, type: 'text', showInDashboard: true },
    // Location columns - READ ONLY (set via import)
    { id: 'area', header: 'Area', accessor: 'area', dbField: 'area', width: 80, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'subArea', header: 'Sub Area', accessor: 'subArea', dbField: 'sub_area', width: 80, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'elevation', header: 'Elevation', accessor: 'elevation', dbField: 'elevation', width: 70, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'floor', header: 'Floor', accessor: 'floor', dbField: 'floor', width: 60, editable: false, filterable: true, type: 'text', showInDashboard: true },
    // Equipment identification - READ ONLY (set via import)
    { id: 'equipmentTagOrCode', header: 'Equipment Tag/Code', accessor: 'equipmentTagOrCode', dbField: 'equipment_tag_or_code', width: 120, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'description', header: 'Description', accessor: 'description', dbField: 'description', width: 150, editable: false, filterable: true, type: 'text', showInDashboard: true },
    // Receipt tracking
    { id: 'dateEquipmentReceived', header: 'Date Equipment Received', accessor: 'dateEquipmentReceived', dbField: 'date_equipment_received', width: 110, editable: true, filterable: true, type: 'date', showInDashboard: true },
    { id: 'equipmentReceiptPercent', header: 'Equipment Receipt', accessor: 'equipmentReceiptPercent', dbField: 'equipment_receipt_percent', width: 90, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], headerColor: '#E3F2FD', headerGroup: 'Receipt', showInDashboard: true },
    // Progress columns (visible in Rules of Credit) - reordered per spec
    { id: 'measureAnchorBushHammer', header: 'Layout/Anchor/Bush Hammer', accessor: 'measureAnchorBushHammer', dbField: 'measure_anchor_bush_hammer', width: 95, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#F3E5F5', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'roughSet', header: 'Rough Set', accessor: 'roughSet', dbField: 'rough_set', width: 80, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#F3E5F5', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'grout', header: 'Grout', accessor: 'grout', dbField: 'grout', width: 65, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#F3E5F5', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'vesselCloseOut', header: 'Vessel Close Out', accessor: 'vesselCloseOut', dbField: 'vessel_close_out', width: 85, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#F3E5F5', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'insulation', header: 'Insulation', accessor: 'insulation', dbField: 'insulation', width: 75, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#F3E5F5', headerGroup: 'Field Progress', showInDashboard: true },
    // Total completion and comments (visible at end)
    { id: 'totalFieldCompletion', header: 'Total Completion', accessor: 'totalFieldCompletion', dbField: 'total_field_completion', width: 90, editable: false, filterable: true, type: 'progress-readonly', computed: true, headerColor: '#E1BEE7', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'comments', header: 'Comments', accessor: 'comments', dbField: 'comments', width: 150, editable: true, filterable: true, type: 'text', showInDashboard: true },
    
    // === HIDDEN COLUMNS (available for import/export and column config) ===
    { id: 'equipmentType', header: 'Type', accessor: 'equipmentType', dbField: 'equipment_type', width: 120, editable: true, filterable: true, type: 'text', showInDashboard: false },
    // Hidden progress columns (data preserved in DB, hidden from UI per spec)
    { id: 'deliveryToSite', header: 'Delivery to Site', accessor: 'deliveryToSite', dbField: 'delivery_to_site', width: 85, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#F3E5F5', headerGroup: 'Field Progress', showInDashboard: false },
    { id: 'supports', header: 'Supports', accessor: 'supports', dbField: 'supports', width: 75, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#F3E5F5', headerGroup: 'Field Progress', showInDashboard: false },
    { id: 'bolting', header: 'Bolting', accessor: 'bolting', dbField: 'bolting', width: 70, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#F3E5F5', headerGroup: 'Field Progress', showInDashboard: false },
    { id: 'accessories', header: 'Accessories', accessor: 'accessories', dbField: 'accessories', width: 80, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#F3E5F5', headerGroup: 'Field Progress', showInDashboard: false },
    // Other hidden columns
    { id: 'id', header: 'ID', accessor: 'id', dbField: 'id', width: 50, editable: false, filterable: true, type: 'number', showInDashboard: false },
    { id: 'buildingNumber', header: 'Building Number', accessor: 'buildingNumber', dbField: 'building_number', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'sovCode', header: 'SOV Code', accessor: 'sovCode', dbField: 'sov_code', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'wbsCode', header: 'WBS Code', accessor: 'wbsCode', dbField: 'wbs_code', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'divisionArea', header: 'Division Area', accessor: 'divisionArea', dbField: 'division_area', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'subDivisionArea', header: 'Sub Division Area', accessor: 'subDivisionArea', dbField: 'sub_division_area', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'jobNumber', header: 'Job Number', accessor: 'jobNumber', dbField: 'job_number', width: 80, editable: false, filterable: true, type: 'text', showInDashboard: false },
    { id: 'activityId', header: 'Activity ID', accessor: 'activityId', dbField: 'activity_id', width: 130, editable: true, filterable: true, type: 'text', showInDashboard: true, headerGroup: 'Schedule' },
    { id: 'activityName', header: 'Activity Name', accessor: 'activityName', dbField: 'activity_name', width: 160, editable: true, filterable: true, type: 'text', showInDashboard: false, headerGroup: 'Schedule' },
    { id: 'budgetedHr', header: 'Budgeted Hr', accessor: 'budgetedHr', dbField: 'budgeted_hr', width: 80, editable: true, filterable: true, type: 'number', showInDashboard: false },
    { id: 'credit', header: 'Credit', accessor: 'credit', dbField: 'credit', width: 70, editable: true, filterable: true, type: 'number', showInDashboard: false },
    { id: 'earnedHr', header: 'Earned Hr', accessor: 'earnedHr', dbField: 'earned_hr', width: 80, editable: false, filterable: true, type: 'number', computed: true, showInDashboard: false },
    { id: 'earnedCredit', header: 'Earned Credit', accessor: 'earnedCredit', dbField: 'earned_credit', width: 90, editable: false, filterable: true, type: 'number', computed: true, showInDashboard: false },
    { id: 'costCode', header: 'Cost Code', accessor: 'costCode', dbField: 'cost_code', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'pAndId', header: 'P&ID', accessor: 'pAndId', dbField: 'p_and_id', width: 70, editable: true, filterable: true, type: 'text', showInDashboard: false },
    { id: 'createdAt', header: 'Created At', accessor: 'createdAt', dbField: 'created_at', width: 130, editable: false, filterable: true, type: 'datetime', showInDashboard: false },
    { id: 'updatedAt', header: 'Updated At', accessor: 'updatedAt', dbField: 'updated_at', width: 130, editable: false, filterable: true, type: 'datetime', showInDashboard: false },
  ],
  
  // UDF (User Defined Fields) columns - hidden by default, added dynamically based on project config
  udfColumns: Array.from({ length: 10 }, (_, i) => ({
    id: `udf${String(i + 1).padStart(2, '0')}`,
    header: `UDF ${String(i + 1).padStart(2, '0')}`,
    accessor: `udf${String(i + 1).padStart(2, '0')}`,
    dbField: `udf${String(i + 1).padStart(2, '0')}`,
    width: 100,
    editable: true,
    filterable: true,
    type: 'text',
    isUdf: true,
    showInDashboard: false,
  })),
  
  // Field mappings
  fieldMap: FIELD_MAP,
  editableColumns: EDITABLE_COLUMNS,
  
  // Data transformation
  transformData: transformMatrixData,
  buildChangePayload: buildChangePayload,
  
  // Available features for this discipline
  features: {
    dragFill: true,
    progressTrends: true,
    matBore: false,              // Not applicable to vessels
    rulesOfCredit: true,
    columnConfig: true,
    actualSpentUpload: false,    // Skip for now
    export: true,
    upload: true,
    presence: true,
    budgetReport: false,         // Not yet implemented
    earnedReport: false,         // Not yet implemented
    evmReport: false,            // Not yet implemented
  },
};

export default vesselsConfig;
